import React from 'react'

import { Divider, Badge } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import {
  array, func, object, number, bool, oneOfType, string,
} from 'prop-types'

import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'

import modelDetails from './Static/modelDetails'
import modelTypes from './Static/modelTypes'

function ModelDetailsTable({
  tags = EMPTY_ARRAY,
  currentSelectedFileTypes,
  setSelectedFilters,
  trackFilterChange,
  types = EMPTY_OBJECT,
  polygons,
  vertices,
  textures = false,
  materials = false,
  uvwMapping = false,
  pluginsUsed = false,
}) {
  const details = {
    textures,
    materials,
    uvwMapping,
    pluginsUsed,
  }

  function triggerFiltersChange(isCurrentlySelected, id, selectedLabel) {
    if (isCurrentlySelected) {
      setSelectedFilters({
        name: 'fileTypes',
        value: currentSelectedFileTypes.filter((item) => item !== `${id}`),
      })
    } else {
      trackFilterChange({
        name: 'fileTypes',
        value: selectedLabel,
        eventSource: 'model preview',
      })
      setSelectedFilters({
        name: 'fileTypes',
        value: [...currentSelectedFileTypes, `${id}`],
      })
    }
  }

  const badges = tags
    .filter(({ name }) => !!name)
    .map(({ name, id }) => {
      const isSelected = currentSelectedFileTypes.includes(`${id}`)
      const props = isSelected ? {
        type: 'primary',
      } : {
        type: 'transparent',
        hasBorder: true,
      }

      const selectedLabel = name.replace(/\./g, '')

      return ({
        isSelected,
        value: selectedLabel,
        ...props,
        onClick: () => triggerFiltersChange(isSelected, id, selectedLabel),
      })
    })
    .sort((a, b) => b.isSelected - a.isSelected)

  function renderFileFormatTags() {
    return badges.map(({
      hasBorder, onClick, type, value,
    }) => (
      <Badge
        className="model-details-table__format-badge"
        hasBorder={hasBorder}
        key={value}
        onClick={onClick}
        type={type}
      >
        {value}
      </Badge>
    ))
  }

  function renderAttributes(list, modelValues) {
    return list.map(({ key, label }) => (
      <Badge
        key={key}
        type={modelValues[key] ? 'light-green' : 'light-red'}
      >
        {label}
      </Badge>
    ))
  }

  function getDetailValue(detail) {
    if (!detail || detail === 'false') {
      return 'Unknown'
    }

    return detail
  }

  return (
    <div className="model-details-table cgt-effect-card-border-disabled">
      <div className="model-details-table__row model-details-table__row--with-no-margin">
        <span className="model-details-table__title cgt-legacy-text-tiny">Formats</span>
        <div className="model-details-table__badges-container">
          {renderFileFormatTags()}
        </div>
      </div>
      <div className="model-details-table__row">
        <span className="model-details-table__title cgt-legacy-text-tiny">Geometry</span>
        <div className="model-details-table__badges-container">
          <Badge
            type="transparent"
            className="model-details-table__tag"
            hasBorder
          >
            {getDetailValue(polygons)} Polygons
          </Badge>
          <Badge
            type="transparent"
            className="model-details-table__tag"
            hasBorder
          >
            {getDetailValue(vertices)} Vertices
          </Badge>
        </div>
      </div>
      <Divider customClass="model-details-table__divider" />
      <div className="model-details-table__row model-details-table__row--with-no-margin">
        <span className="model-details-table__title cgt-legacy-text-tiny">Type</span>
        <div className="model-details-table__badges-container">
          {renderAttributes(modelTypes, types)}
        </div>
      </div>
      <div className="model-details-table__row">
        <span className="model-details-table__title cgt-legacy-text-tiny">Details</span>
        <div className="model-details-table__badges-container">
          {renderAttributes(modelDetails, details)}
        </div>
      </div>
    </div>
  )
}

ModelDetailsTable.propTypes = {
  tags: array,
  types: object,
  currentSelectedFileTypes: array.isRequired,
  setSelectedFilters: func.isRequired,
  trackFilterChange: func.isRequired,
  polygons: oneOfType([number, string, bool]),
  vertices: oneOfType([number, string, bool]),
  textures: bool,
  materials: bool,
  uvwMapping: bool,
  pluginsUsed: bool,
}

export default ModelDetailsTable
